import { getPriceKeyParams, isMobile, planPricingKey } from "@/helpers/index";
import { SUBSCRIPTION_KEYS, DOWNGRADE_PLAN_FORM_LINK, BILLING_FREQUENCY } from "./const";
import { isFreeBetaPlan, isFreePlan, isTrialPlan } from "@/helpers/plansHelpers";
import { isDateInPast } from "./date";
import { PRODUCT_TO_STATE_KEY_MAP } from "@/const/product.constant";

// @deprecated - use PRODUCTS from "@/const/product.constant" instead
export const PRODUCTS = {
  MAIL_SUITE: "MailSuite",
  SITE: "Site",
  DOMAIN: "NeoDomain",
};

export const ADDON = "addon";

export const getProductDefaultRoute = products => {
  if (hasMailSuiteProduct(products)) return "/email-accounts";

  if (hasSiteProduct(products)) return "/manage-site";

  return "/product-not-found";
};

export const hasMailSuiteProduct = products => {
  return products.indexOf(PRODUCTS.MAIL_SUITE) > -1;
};

export const hasSiteProduct = products => {
  return products.indexOf(PRODUCTS.SITE) > -1;
};

export const hasDomainProduct = products => {
  return products.indexOf(PRODUCTS.DOMAIN) > -1;
};

export const isMailSuiteProduct = product => {
  return product === PRODUCTS.MAIL_SUITE;
};

export const isSiteProduct = product => {
  return product === PRODUCTS.SITE;
};

export const isDomainProduct = product => {
  return product === PRODUCTS.DOMAIN;
};

export const getProductsFromCurrentPlan = currentPlanDetails => {
  const products = [];

  if (currentPlanDetails?.mailSuiteDetails?.planId) {
    products.push(PRODUCTS.MAIL_SUITE);
  }

  if (currentPlanDetails?.siteDetails?.planId) {
    products.push(PRODUCTS.SITE);
  }

  if (currentPlanDetails?.domainDetails?.planId) {
    products.push(PRODUCTS.DOMAIN);
  }

  return products;
};

export const getPricingConfigIdsForProducts = currentPlanDetails => {
  const pricingConfigIds = {};

  if (currentPlanDetails?.mailSuiteDetails?.pricingConfigId) {
    pricingConfigIds[PRODUCTS.MAIL_SUITE] = currentPlanDetails?.mailSuiteDetails?.pricingConfigId;
  }

  if (currentPlanDetails?.siteDetails?.pricingConfigId) {
    pricingConfigIds[PRODUCTS.SITE] = currentPlanDetails?.siteDetails?.pricingConfigId;
  }

  if (currentPlanDetails?.domainDetails?.pricingConfigId) {
    pricingConfigIds[PRODUCTS.DOMAIN] = currentPlanDetails?.domainDetails?.pricingConfigId;
  }

  return pricingConfigIds;
};

// FIXME: as per the function context, it also should return domain details
// check the impact and return domain details here
export const getAllProductsDetails = currentPlanDetails => {
  const productsDetails = [];

  if (currentPlanDetails?.mailSuiteDetails?.planId) {
    productsDetails.push(currentPlanDetails?.mailSuiteDetails);
  }

  if (currentPlanDetails?.siteDetails?.planId) {
    productsDetails.push(currentPlanDetails?.siteDetails);
  }

  return productsDetails;
};

/**
 * Transforms and combines product details, plan details, and trial information
 * into a single object for further use.
 * If the plan is a trial, additional paid plan info is included.
 */
export const formatProductDetails = (paidPlan, productPlanDetails, productDetails) => {
  return {
    ...productDetails,
    status: deriveSubStatusFromFlags(productDetails.flags),
    planDetails: productPlanDetails,
    isTrial: productPlanDetails.isTrial,
    isPaid: productPlanDetails.isPaid,
    isFreeBetaPlan: isFreeBetaPlan(productPlanDetails?.planDetails),
    isProductSuspended: productDetails.isProductSuspended,
    ...(productPlanDetails.isTrial
      ? {
          paidPlanId: productPlanDetails.paidPlanId,
          paidPlan,
        }
      : {}),
  };
};

export const formatDomainDetails = (
  paidPlan,
  productPlanDetails,
  productDetails,
  currentDomain
) => {
  return {
    ...currentDomain,
    ...formatProductDetails(paidPlan, productPlanDetails, productDetails),
    planPricing:
      // domainPricing for backward compatibility
      productPlanDetails?.planPricing || productPlanDetails?.domainPricing,
  };
};

/**
 *
 * @param {array} flags - The Flags for current plan.
 * @returns {boolean} Returns true if current plan has subscription cancelled key includes in array; otherwise false
 */
export const isCancelSubFlagPresent = flags => {
  return flags?.includes(SUBSCRIPTION_KEYS.CANCEL_SUBSCRIPTION);
};

export const isReactiveSubFlagPresent = domainFlags => {
  return domainFlags?.includes(SUBSCRIPTION_KEYS.REACTIVATE_SUBSCRIPTION);
};

export const isRenewalBillingFreqMonthly = currentPlanDetails => {
  return currentPlanDetails?.renewalBillingCycle === BILLING_FREQUENCY.MONTHLY;
};

/**
 *
 * @param {array} flags - The Flags for current plan.
 * @returns {boolean} Returns true if current plan has CANCELLATION_BENEFIT_USED key includes i array, otherwise false
 */
export const isCancellationBenefitUsedSubFlagPresent = flags => {
  return flags?.includes(SUBSCRIPTION_KEYS.CANCELLATION_BENEFIT_USED);
};

/**
 * Determines the subscription status based on specific flags
 * @param {Array} flags - SUBSCRIPTION_KEYS
 * @returns {String} SUBSCRIPTION_KEYS[key]
 */
export const deriveSubStatusFromFlags = flags => {
  if (isCancelSubFlagPresent(flags)) return SUBSCRIPTION_KEYS.CANCEL_SUBSCRIPTION;

  return SUBSCRIPTION_KEYS.ACTIVE;
};

export const isActiveProduct = productDetails => {
  return (
    productDetails?.planId &&
    !productDetails?.flags?.includes(SUBSCRIPTION_KEYS.CANCEL_SUBSCRIPTION) &&
    !productDetails?.isProductSuspended
  );
};

export const getActiveProducts = currentPlanDetails => {
  const activeProducts = [];

  PRODUCT_TO_STATE_KEY_MAP.forEach(({ key, product }) => {
    if (isActiveProduct(currentPlanDetails?.[key])) {
      activeProducts.push(product);
    }
  });

  return activeProducts;
};

// Returns the list of products that can be renewed
export const getRenewableProducts = currentPlanDetails => {
  const renewableProducts = [];

  PRODUCT_TO_STATE_KEY_MAP.forEach(({ key, product }) => {
    const productDetails = currentPlanDetails?.[key];
    // product is active and not free (free, free_site, site_beta, freeplan_site)
    if (
      isActiveProduct(productDetails) &&
      !isFreePlan(productDetails?.planDetails) &&
      // skip trial products who expiry is not matching with bundle
      !(
        isTrialPlan(productDetails?.planDetails) &&
        currentPlanDetails.expiresOn !== productDetails.expiry
      )
    ) {
      renewableProducts.push(product);
    }
  });

  return renewableProducts;
};

// True if the bundle has at least one addon that will renew on expiry (a cancelled addon has renewOnExpiry === false)
export const hasRenewableAddon = currentPlanDetails =>
  (currentPlanDetails?.addons || []).some(addon => addon.renewOnExpiry);

/**
 * Checks if the product to cancel is the last cancellable product.
 *
 * @param {Array<string>} activeProducts - The list of active products.
 * @param {string} productToCancel - The product to cancel.
 * @param isSiteFreeBeta
 * @returns {boolean} - Returns true if, after removing the product to cancel and PRODUCTS.DOMAIN, the length of active products is 0.
 */
export const isLastCancellableProduct = (activeProducts, productToCancel, isSiteFreeBeta) => {
  const filteredProducts = activeProducts.filter(
    product => product !== productToCancel && product !== PRODUCTS.DOMAIN
  );

  if (isSiteFreeBeta) {
    return filteredProducts.filter(product => product !== PRODUCTS.SITE).length === 0;
  }
  return filteredProducts.length === 0;
};

export const isProductCancelled = productPurchaseStatus => {
  return productPurchaseStatus === SUBSCRIPTION_KEYS.CANCEL_SUBSCRIPTION;
};

export const areAllProductsCancelled = products => {
  return products.filter(p => p?.planId).every(product => isProductCancelled(product.status));
};

/**
 * Generates an order configuration object for flags.
 *
 * @param {Array<string>} products - The list of products.
 * @param {'set' | 'unset'} action - The action to perform on the flags, either 'set' or 'unset'.
 * @param {Array} keysArray - The array of keys to set or unset.
 * @returns {Object} - The order configuration object.
 */
export const getOrderConfigForProducts = (products, action, keysArray) => {
  if (action !== "set" && action !== "unset") {
    throw new Error("Invalid orderConfig action provided.");
  }

  const orderConfig = {};

  const actionsMap = {
    set: "setFlags",
    unset: "unsetFlags",
  };

  products.forEach(product => {
    orderConfig[product] = {
      [actionsMap[action]]: keysArray,
    };
  });

  return orderConfig;
};

/**
 * Generates a URL for downgrading a plan with the specified parameters.
 *
 * @param {Object} params - Parameters for generating the URL.
 * @param {string} params.domainName - The domain name to include in the URL.
 * @param {string} params.product - The product to include in the URL.
 * @param {'cancel' | 'downgrade'} params.flowType - The flow type.
 * @param {string} params.billingCycle - The billing cycle of the product.
 * @param {number} params.planId - The plan ID of the product.
 * @param {number} params.price - The current renewal price of the product.
 * @returns {string} The generated URL with the provided parameters.
 */
export const getDowngradePlanLink = params => {
  const baseUrl = DOWNGRADE_PLAN_FORM_LINK;
  const device = isMobile() ? "mobile" : "desktop";

  // Mapping from params keys to query parameter keys
  const keyMapping = {
    domainName: "domain",
    product: "order",
    flowType: "flow_type",
    billingCycle: "billing_cycle",
    planId: "plan_id",
    price: "price",
    currentPrice: "current_price",
    renewalPrice: "renewal_price",
    // Add more mappings if more params are added
  };

  // Create an object to hold the query parameters
  const queryParams = {};

  // Map the params to query parameters using the keyMapping
  for (const [paramKey, paramValue] of Object.entries(params)) {
    if (paramValue) {
      // Use the mapped key if it exists, otherwise use the original key
      const queryKey = keyMapping[paramKey] || paramKey;
      queryParams[queryKey] = paramValue;
    }
  }

  // Add the device parameter
  queryParams.device = device;

  // Use URLSearchParams to construct the query string
  const urlParams = new URLSearchParams(queryParams);

  // Return the final URL
  return `${baseUrl}?${urlParams.toString()}`;
};

export const isBundleSubscriptionCancelled = productDetailsArr => {
  return productDetailsArr
    .filter(p => p?.planId)
    .every(product => isCancelSubFlagPresent(product.flags));
};

/**
 * Retrieves the current price for a given product based on the current plan details.
 *
 * @param {Object} productDetails - The details of the product.
 * @param {Object} currentPlanDetails - The details of the current plan.
 * @returns {number} - The current price of the product.
 */
export const getCurrentPriceForProduct = (productDetails, currentPlanDetails) => {
  const pricingKey = planPricingKey(getPriceKeyParams(currentPlanDetails, productDetails));

  return productDetails.planDetails.planPricing[currentPlanDetails.billingCycle]?.[pricingKey];
};

/**
 * Retrieves the current price for a given product based on the current plan details.
 *
 * @param {Array} orders - The array of orders in the bundle.
 * @param {string} productName - The name of the product - 'MailSuite' || 'Site' || 'NeoDomain'.
 * @returns {Object | null} - Order details of the specified product. Returns null if not found.
 */

export const getOrderDetailsOfProduct = (orders, productName) => {
  if (!Array.isArray(orders) || !orders.length) {
    return null;
  }

  return orders.find(order => order.type === productName) || null;
};

/**
 * function to check if product expiry is before bundle expiry
 * @param {*} productDetails <product>.currentProductDetails
 * @param {*} currentPlan store.currentPlanDetails
 * @returns {boolean} true if product expired before bundle, false otherwise
 */
export const isProductExpiredBeforeBundle = (productDetails, currentPlan) => {
  if (!productDetails?.expiry || !currentPlan?.expiresOn) return false;

  const productExpiry = new Date(productDetails?.expiry);
  const bundleExpiry = new Date(currentPlan.expiresOn);

  return isDateInPast(productExpiry) && !isDateInPast(bundleExpiry);
};
